/**
 * Program 12.1 UX3 — Early-pursuit attention extensions for pursuit workspace.
 */
import type { Opportunity } from '@/services/opportunity.service';
import type { PursuitAttentionItem } from './pursuitWorkspaceModel';
import { weightedPipeline, parseEpcContext } from './epcContextTypes';

export function buildEarlyPursuitAttention(input: {
  opportunity?: Opportunity | null;
  prequals?: Record<string, unknown>[];
  tenders?: Record<string, unknown>[];
}): PursuitAttentionItem[] {
  const items: PursuitAttentionItem[] = [];
  const opp = input.opportunity;
  if (!opp) return items;

  const ctx = parseEpcContext((opp as { epc_context_json?: unknown }).epc_context_json);

  if (!opp.estimated_value || Number(opp.estimated_value) <= 0) {
    items.push({
      id: 'opp-no-value',
      severity: 'info',
      title: 'Opportunity value not established',
      stageKey: 'OPPORTUNITY',
    });
  }

  if (!ctx.end_user) {
    items.push({
      id: 'opp-no-end-user',
      severity: 'info',
      title: 'End user not identified',
      stageKey: 'OPPORTUNITY',
    });
  }

  const weighted = weightedPipeline(opp.estimated_value, opp.probability);
  if (weighted != null && weighted > 0) {
    items.push({
      id: 'opp-weighted-pipeline',
      severity: 'info',
      title: 'Weighted pipeline calculated',
      detail: `Deterministic from value × probability`,
      stageKey: 'OPPORTUNITY',
    });
  }

  for (const pq of input.prequals || []) {
    const due = pq.due_date || pq.dueDate;
    if (due) {
      const d = new Date(String(due).slice(0, 10));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.ceil((d.getTime() - today.getTime()) / 86400000);
      if (days >= 0 && days <= 3) {
        items.push({
          id: `pq-due-${pq.id}`,
          severity: 'warning',
          title: 'Prequalification deadline approaching',
          detail: `${days} day(s) remaining`,
          stageKey: 'PREQUALIFICATION',
        });
      }
    }
    if (String(pq.tender_gate || pq.tenderGate) === 'BLOCK') {
      items.push({
        id: `pq-block-${pq.id}`,
        severity: 'critical',
        title: 'Prequalification blocks tender',
        stageKey: 'PREQUALIFICATION',
      });
    }
  }

  for (const t of input.tenders || []) {
    if (!t.submission_due_at && !t.submissionDueAt) {
      items.push({
        id: `tender-no-deadline-${t.id}`,
        severity: 'warning',
        title: 'Tender submission date missing',
        stageKey: 'TENDER',
      });
    }
  }

  return items;
}
